import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Check } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const PERKS = ["Free forever — no fees", "Start at 15% commission per click value", "$25 ad credits after verification", "Ready-made ads + social growth support"];

export default function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await register(form.name, form.email, form.password);
      toast.success(data.verification_email_sent ? "Account created! Check your inbox to verify your email." : "Account created!");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="max-w-4xl mx-auto px-4 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div>
        <p className="uppercase text-xs tracking-[0.2em] font-semibold text-emerald-600 mb-3 flex items-center gap-1.5"><Sparkles className="w-4 h-4" /> Join Social Flats</p>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-6">Your affiliate account. Free.</h1>
        <ul className="space-y-3">
          {PERKS.map((p) => (
            <li key={p} className="flex gap-3 text-sm text-slate-600"><Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{p}</li>
          ))}
        </ul>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-10">
        {error && <p data-testid="signup-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-4 py-3 mb-5">{error}</p>}
        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Name</label>
            <input required maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="signup-name-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="signup-email-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <div>
            <label className="uppercase text-xs tracking-[0.2em] font-semibold text-slate-500 block mb-2">Password (8+ characters)</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="signup-password-input"
              className="w-full px-4 py-3 rounded-lg bg-slate-50 border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          </div>
          <button type="submit" disabled={loading} data-testid="signup-submit-button"
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-emerald-600 text-white font-medium hover:bg-emerald-500 transition-colors duration-200 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create free account
          </button>
        </form>
        <p className="text-sm text-slate-500 mt-6 text-center">
          Already a member? <Link to="/login" data-testid="signup-to-login-link" className="font-medium text-emerald-700 hover:text-emerald-800 transition-colors duration-200">Log in</Link>
        </p>
      </div>
    </main>
  );
}
